import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { motion, AnimatePresence } from 'motion/react';
import { Inbox as InboxIcon, RefreshCw, ChevronRight } from 'lucide-react';

interface InboxProps { user: User; }

interface RetakeItem {
  id: string;
  test_id: string;
  recipient: string;
  platform: string;
  status: string;
  created_at: string;
  partner_name?: string;
  retake_count?: number;
}

export default function InboxPage({ user }: InboxProps) {
  const [items, setItems] = useState<RetakeItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchInbox(); }, []);

  const fetchInbox = async () => {
    setLoading(true);
    const { data: tests } = await supabase
      .from('tests')
      .select('id, partner_name, retake_count')
      .eq('creator_id', user.id);

    if (!tests?.length) { setLoading(false); return; }

    const testIds = tests.map(t => t.id);
    const { data: notifications } = await supabase
      .from('retake_notifications')
      .select('id, test_id, recipient, platform, status, created_at')
      .in('test_id', testIds)
      .order('created_at', { ascending: false });

    if (!notifications) { setLoading(false); return; }

    const testsMap = Object.fromEntries(tests.map(t => [t.id, t]));
    setItems(notifications.map(n => ({
      ...n,
      partner_name: testsMap[n.test_id]?.partner_name,
      retake_count: testsMap[n.test_id]?.retake_count,
    })));

    // Mark all pending as read (viewed) by updating status to 'viewed' if still pending
    const pendingIds = notifications.filter(n => n.status === 'pending').map(n => n.id);
    if (pendingIds.length) {
      await supabase
        .from('retake_notifications')
        .update({ status: 'viewed' })
        .in('id', pendingIds);
    }

    setLoading(false);
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="max-w-2xl mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <InboxIcon className="w-4 h-4 text-brand-accent" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-accent">Retake Inbox</span>
          </div>
          <h1 className="text-3xl font-light text-brand-text leading-tight">
            Retake <span className="font-serif italic text-zinc-500">Requests</span>
          </h1>
        </div>
        <button
          onClick={fetchInbox}
          className="p-2 rounded-xl border border-brand-border text-zinc-500 hover:text-brand-accent transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-brand-card rounded-2xl animate-pulse border border-brand-border" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border border-brand-border rounded-2xl bg-brand-card">
          <InboxIcon className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500 text-sm">No retake requests yet.</p>
          <p className="text-zinc-600 text-xs mt-1">When you rate someone below 6, it shows up here.</p>
        </div>
      ) : (
        <AnimatePresence initial={false}>
          <div className="space-y-3">
            {items.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-brand-card border border-brand-border rounded-2xl p-5 flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {item.status === 'pending' && (
                    <span className="w-2 h-2 rounded-full bg-brand-accent shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-light text-brand-text truncate">
                      <span className="font-semibold">{item.partner_name}</span>
                      <span className="text-zinc-500 mx-1">·</span>
                      <span className="text-zinc-500">{item.platform}</span>
                      <span className="text-zinc-600 text-xs ml-2">
                        {item.retake_count && item.retake_count > 0 ? 'retook quiz after rating' : 'rated · message ready to send'}
                      </span>
                    </p>
                    <p className="text-[10px] text-zinc-600 mt-0.5">{timeAgo(item.created_at)}</p>
                  </div>
                </div>
                <Link
                  to={`/results/${item.test_id}`}
                  className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-brand-accent transition-colors shrink-0"
                >
                  View <ChevronRight className="w-3 h-3" />
                </Link>
              </motion.div>
            ))}
          </div>
        </AnimatePresence>
      )}
    </div>
  );
}
